import { defineStore } from "pinia";
import { ref } from "vue";
import type { PageModel } from "../types/shopify";
import { pageFragment, seoFragment } from "../composables/shopify";
import { useApolloClient } from "@vue/apollo-composable";
import gql from "graphql-tag";



const GET_PAGE_BY_HANDLE = gql`
  query GetPage($handle: String!) {
    page(handle: $handle) {
      ...page
    }
  }
  ${pageFragment}
  ${seoFragment}
`;

const GET_PAGE_BY_ID = gql`
  query GetPageByID($id: ID!) {
    node(id: $id) {
      ... on Page {
        ...page
      }
    }
  }
  ${pageFragment}
  ${seoFragment}
`;

const GET_MEDIA_IMAGE_BY_ID = gql`
  query MediaImageWithFocal($id: ID!) {
    node(id: $id) {
      ... on MediaImage {
        id
        image {
          url
          altText
        }
        presentation {
          asJson(format: IMAGE)
        }
      }
    }
  }
`;

export const usePageStore = defineStore("page", () => {
  const pagesByHandle = ref<Record<string, PageModel>>({});
  const pagesById = ref<Record<string, PageModel>>({});

  function setPageByHandle(handle: string, page: PageModel) {
    pagesByHandle.value[handle] = page;
  }

  function setPageById(id: string, page: PageModel) {
    pagesById.value[id] = page;
  }

  function getPageByHandle(handle: string): PageModel | undefined {
    return pagesByHandle.value[handle];
  }

  function getPageById(id: string): PageModel | undefined {
    return pagesById.value[id];
  }

  async function fetchAllPages() {
    const query = gql`
      query getPages {
        pages(first: 100) {
          edges {
            node {
              ...page
            }
          }
        }
      }
      ${pageFragment}
      ${seoFragment}
    `;

    try {
      const { client } = useApolloClient();
      const { data } = await client.query({ query });

      if (data?.pages?.edges) {
        data.pages.edges.forEach(({ node }: { node: PageModel }) => {
          const page: PageModel = {
            ...node,
            coverImage: node.coverImage ? { ...node.coverImage } : undefined,
          };

          fetchPageByHandle(page.handle);
        });
      }
    } catch (error) {
      console.error("Error fetching all pages:", error);
    }
  }

  async function fetchPageByHandle(handle: string): Promise<PageModel | null> {
    if (pagesByHandle.value[handle]) {
      return pagesByHandle.value[handle];
    }

    try {
      const { client } = useApolloClient();
      const { data } = await client.query({
        query: GET_PAGE_BY_HANDLE,
        variables: { handle },
      });

      if (!data || !data.page) {
        console.warn(`No page data found for handle: ${handle}`);
        return null;
      }

      const originalPage = data.page as PageModel;
      const newPage: PageModel = {
        ...originalPage,
        coverImage: originalPage.coverImage ? { ...originalPage.coverImage } : undefined,
      };

      const coverImageId = newPage.coverImage?.value;
      if (coverImageId) {
        try {
          const { data: mediaData } = await client.query({
            query: GET_MEDIA_IMAGE_BY_ID,
            variables: { id: coverImageId },
          });

          if (mediaData?.node && newPage.coverImage) {
            newPage.coverImage.reference = mediaData.node;
          }
        } catch (mediaError) {
          console.warn('Kunde inte hämta media-bild för coverImage:', mediaError);
        }
      }

      setPageByHandle(handle, newPage);
      return newPage;
    } catch (error) {
      console.error(`Error fetching page for handle ${handle}:`, error);
      return null;
    }
  }

  async function fetchPageByID(id: string): Promise<PageModel | null> {
    if (pagesById.value[id]) {
      return pagesById.value[id];
    }

    try {
      const { client } = useApolloClient();
      const { data } = await client.query({
        query: GET_PAGE_BY_ID,
        variables: { id },
      });

      if (!data || !data.node) {
        console.warn(`No page data found for ID: ${id}`);
        return null;
      }

      if (data.node.__typename !== "Page") {
        console.warn(`Node with ID ${id} is not a Page, it is a ${data.node.__typename}`);
        return null;
      }

      const originalPage = data.node as PageModel;
      const newPage: PageModel = {
        ...originalPage,
        coverImage: originalPage.coverImage ? { ...originalPage.coverImage } : undefined,
      };

      const coverImageId = newPage.coverImage?.value;
      if (coverImageId) {
        try {
          const { data: mediaData } = await client.query({
            query: GET_MEDIA_IMAGE_BY_ID,
            variables: { id: coverImageId },
          });

          if (mediaData?.node && newPage.coverImage) {
            newPage.coverImage.reference = mediaData.node;
          }
        } catch (mediaError) {
          console.warn('Kunde inte hämta media-bild för coverImage:', mediaError);
        }
      }

      setPageById(id, newPage);
      setPageByHandle(newPage.handle, newPage);

      return newPage;
    } catch (error) {
      console.error(`Error fetching page for ID ${id}:`, error);
      return null;
    }
  }

  return {
    pagesByHandle,
    fetchAllPages,
    setPageByHandle,
    getPageByHandle,
    fetchPageByHandle,
    fetchPageByID    
  };
});
